/**
 * GlobalSearch — command-palette style search embedded in AppHeader.
 *
 * - Debounced fetch to /api/search?q=
 * - Keyboard: "/" to focus, Esc to dismiss, Enter on first result
 * - Results grouped by type: Companies, Investigations, Users
 * - Tenant-safe — backend filters by actor tenant automatically
 */
import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

const API = process.env.REACT_APP_BACKEND_URL;
const MIN_CHARS = 2;
const DEBOUNCE_MS = 250;

export default function GlobalSearch() {
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [error, setError] = useState(null);
  const [active, setActive] = useState(0);
  const containerRef = useRef(null);
  const inputRef = useRef(null);
  const navigate = useNavigate();

  // Global "/" hotkey to focus the search
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === '/' && !['INPUT', 'TEXTAREA'].includes(document.activeElement?.tagName)) {
        e.preventDefault();
        inputRef.current?.focus();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // Close on outside click
  useEffect(() => {
    const onClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  // Debounced fetch
  useEffect(() => {
    if (q.trim().length < MIN_CHARS) { setResults(null); return; }
    let ignore = false;
    setLoading(true); setError(null);
    const t = setTimeout(async () => {
      try {
        const r = await fetch(`${API}/api/search?q=${encodeURIComponent(q.trim())}`);
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const d = await r.json();
        if (!ignore) { setResults(d); setActive(0); }
      } catch (e) { if (!ignore) setError(e.message); }
      finally { if (!ignore) setLoading(false); }
    }, DEBOUNCE_MS);
    return () => { ignore = true; clearTimeout(t); };
  }, [q]);

  const flat = useCallback(() => {
    if (!results) return [];
    return [
      ...(results.companies || []).map((c) => ({ kind: 'company', ...c })),
      ...(results.investigations || []).map((i) => ({ kind: 'investigation', ...i })),
      ...(results.users || []).map((u) => ({ kind: 'user', ...u })),
    ];
  }, [results]);

  const navigateTo = (item) => {
    setOpen(false); setQ('');
    if (item.kind === 'company') {
      navigate(`/?view=pipeline&company=${item.id}`);
    } else if (item.kind === 'investigation') {
      navigate(`/investigation/${item.id}`);
    } else if (item.kind === 'user') {
      // Users live on the admin Users list; backend already restricted the
      // search results to roles allowed to see users, so any user who got a
      // user-result back is also allowed to view /users.
      navigate(`/users?focus=${item.id}`);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === 'Escape') { setOpen(false); inputRef.current?.blur(); return; }
    const items = flat();
    if (!items.length) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setActive((i) => Math.min(i + 1, items.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive((i) => Math.max(i - 1, 0)); }
    else if (e.key === 'Enter') { e.preventDefault(); navigateTo(items[active]); }
  };

  const total = results ? (results.counts?.companies + results.counts?.investigations + results.counts?.users) : 0;

  return (
    <div ref={containerRef} className="global-search" data-testid="global-search"
         style={{ position: 'relative', flex: '1 1 200px', minWidth: 0, maxWidth: 520, margin: '0 12px' }}>
      <input
        ref={inputRef}
        type="search"
        placeholder="Search companies, investigations, users…  ( / )"
        value={q}
        onChange={(e) => { setQ(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKeyDown}
        data-testid="global-search-input"
        style={{
          width: '100%', minWidth: 0, padding: '6px 10px', fontSize: 12,
          border: '1px solid var(--border)', borderRadius: 6,
          background: 'var(--bg)',
        }}
      />
      {open && q.trim().length >= MIN_CHARS && (
        <div className="global-search-results" data-testid="global-search-results"
             style={{
               position: 'absolute', top: '100%', left: 0, right: 0, marginTop: 4,
               background: 'var(--panel, #fff)', border: '1px solid var(--border)',
               borderRadius: 6, boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
               maxHeight: 380, overflowY: 'auto', zIndex: 100,
             }}>
          {loading && <div style={{ padding: 10, fontSize: 12, color: 'var(--text-secondary)' }}>Searching…</div>}
          {error && <div className="auth-error" style={{ margin: 8 }}>{error}</div>}
          {!loading && !error && results && total === 0 && (
            <div style={{ padding: 12, fontSize: 12, color: 'var(--text-secondary)' }} data-testid="global-search-empty">
              <div style={{ fontWeight: 600, color: 'var(--text)', fontSize: 13 }}>No matches for "{q}"</div>
              <div style={{ marginTop: 4 }}>
                Try a company name, a saved investigation label, or an email address.
              </div>
            </div>
          )}
          {!loading && !error && results && total > 0 && (() => {
            const items = flat();
            let idx = -1;
            const Section = (title, kind) => {
              const slice = items.filter((i) => i.kind === kind);
              if (!slice.length) return null;
              return (
                <div key={kind}>
                  <div style={{
                    padding: '6px 10px', fontSize: 10, fontWeight: 700, textTransform: 'uppercase',
                    letterSpacing: '0.06em', color: 'var(--text-secondary)',
                    background: 'var(--bg)', borderBottom: '1px solid var(--border)',
                  }}>{title} · {slice.length}</div>
                  {slice.map((it) => {
                    idx += 1;
                    const isActive = idx === active;
                    return (
                      <div key={`${kind}-${it.id}`}
                           onClick={() => navigateTo(it)}
                           onMouseEnter={() => setActive(items.indexOf(it))}
                           data-testid={`global-search-item-${kind}-${it.id}`}
                           style={{
                             padding: '8px 10px', cursor: 'pointer',
                             background: isActive ? 'var(--bg)' : 'transparent',
                             borderBottom: '1px solid var(--border)',
                             fontSize: 12,
                           }}>
                        {kind === 'company' && (
                          <>
                            <div style={{ fontWeight: 600 }}>{it.name}</div>
                            <div style={{ fontSize: 10, color: 'var(--text-secondary)' }}>
                              {it.website || '—'} {it.sector ? `· ${it.sector}` : ''}
                            </div>
                          </>
                        )}
                        {kind === 'investigation' && (
                          <>
                            <div style={{ fontWeight: 600 }}>{it.label || 'Untitled investigation'}</div>
                            <div style={{ fontSize: 10, color: 'var(--text-secondary)' }}>
                              {it.company_name || '—'} {it.lens ? `· ${it.lens.replace(/_/g, ' ')}` : ''}
                            </div>
                          </>
                        )}
                        {kind === 'user' && (
                          <>
                            <div style={{ fontWeight: 600 }}>{it.name || it.email}</div>
                            <div style={{ fontSize: 10, color: 'var(--text-secondary)' }}>
                              {it.email} {it.role ? `· ${it.role}` : ''}
                            </div>
                          </>
                        )}
                      </div>
                    );
                  })}
                </div>
              );
            };
            return <>{Section('Companies', 'company')}{Section('Investigations', 'investigation')}{Section('Users', 'user')}</>;
          })()}
        </div>
      )}
    </div>
  );
}
